import { Alert, App as AntApp, DatePicker, Form, Input, Modal, Select } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { exitKeys, useLeavingReasons } from "../app/exits.ts";
import type { StudentProfile } from "../app/students.ts";
import { placementKeys } from "./PlacementModal.tsx";

const noteMaxLength = 2000;

/** Field names match the API (reasonId, leftOn, note) so server field errors land under the right input. */
interface ExitForm {
  reasonId?: string;
  leftOn?: Dayjs;
  note?: string;
}

interface Props {
  student: StudentProfile;
  open: boolean;
  onClose(): void;
}

/** Records that the student leaves: reason from the school's list, leave date and a note. The API also closes the open class placement. */
export function StudentExitModal({ student, open, onClose }: Props) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ExitForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const reasons = useLeavingReasons();
  const active = (reasons.data ?? []).filter((r) => r.isActive);
  const noReasons = reasons.isSuccess && active.length === 0;

  // The form is not rendered while the reason list is empty, so it is only reset once it is on screen.
  useEffect(() => {
    if (open && !noReasons) {
      form.resetFields();
      form.setFieldsValue({ leftOn: dayjs() });
      setSummary([]);
    }
  }, [open, noReasons, form]);

  const mutation = useMutation({
    mutationFn: async (values: ExitForm) => {
      const { data, error, response } = await api.POST("/students/{id}/exit", {
        params: { path: { id: student.id } },
        body: { reasonId: values.reasonId ?? null, leftOn: values.leftOn?.format("YYYY-MM-DD") ?? null, note: values.note?.trim() || null },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: exitKeys.exits(student.id) }),
        queryClient.invalidateQueries({ queryKey: ["students", student.id], exact: true }),
        queryClient.invalidateQueries({ queryKey: placementKeys.history(student.id) }),
        queryClient.invalidateQueries({ queryKey: placementKeys.rosters }),
        queryClient.invalidateQueries({ queryKey: ["families"] }),
        queryClient.invalidateQueries({ queryKey: ["audit"] }),
      ]);
      void message.success(t("exit.recorded"));
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const previous = student.statusChangedOn ? dayjs(student.statusChangedOn) : null;
  const required = [{ required: true, message: t("family.validation.required") }];
  return (
    <Modal
      open={open}
      title={t("exit.title", { name: `${student.lastName} ${student.firstName}` })}
      okText={t("exit.submit")}
      okButtonProps={{ danger: true, disabled: noReasons }}
      cancelText={t("family.cancel")}
      confirmLoading={mutation.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      {reasons.isError && <Alert type="error" showIcon style={{ marginBottom: 16 }} title={t("app.error")} />}
      {noReasons ? (
        <Alert type="warning" showIcon title={t("exit.noReasons")} description={<Link to="/leaving-reasons">{t("exit.manageReasons")}</Link>} />
      ) : (
        <>
          <Alert type="info" showIcon style={{ marginBottom: 12 }} title={t("exit.hint")} />
          <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("student.status.billingHint")} />
          <Form<ExitForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => mutation.mutate(values)}>
            <Form.Item name="reasonId" label={t("exit.reason")} rules={required}>
              <Select loading={reasons.isPending} options={active.map((r) => ({ value: r.id, label: r.name }))} showSearch={{ optionFilterProp: "label" }} />
            </Form.Item>
            <Form.Item name="leftOn" label={t("exit.leftOn")} rules={required}>
              <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" allowClear={false} disabledDate={(d) => (previous ? d.isBefore(previous, "day") : false)} />
            </Form.Item>
            <Form.Item name="note" label={t("exit.note")}>
              <Input.TextArea rows={3} maxLength={noteMaxLength} showCount />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}
